<script lang="ts">
  import { createGetSubscriptionQuery } from "../../queries/subscriptions";
  import ButtonNew from "../buttons/ButtonNew.svelte";
  import ListIcon from "../icons/ListIcon.svelte";
  import EmptyStateBasic from "./EmptyStateBasic.svelte";
  import {
    hasInactiveSubscription,
    hasMinPremiumSubscription,
    openSubscriptionModal,
  } from "../../helpers/subscription";
  import type { GetSubscriptionResponse } from "../../helpers/types";
  import { STRIPE_CUSTOMER_PORTAL_URL } from "../../helpers/constants";
  import WhiteboardIcon from "../icons/WhiteboardIcon.svelte";
  import DigitizeIcon from "../icons/DigitizeIcon.svelte";
  import PriceAlertIcon from "../icons/PriceAlertIcon.svelte";
  import CreditCardIcon from "../icons/CreditCardIcon.svelte";
  import { createModal } from "../modals/createModal";
  import InvoiceUploadModal from "../invoices/InvoiceUploadModal.svelte";
  import { t } from "../../helpers/translations";
  import { selectedBuyer } from "../../stores/buyer";

  type EmptyStateData = {
    title: string;
    subtitle: string;
    status: "active" | "inactive" | "none";
  };

  export let variant: "overview" | "invoices" | "priceHistory" | "priceHistoryChangesOnly";

  $: buyerID = $selectedBuyer?.id ?? "";
  $: subscriptionQuery = createGetSubscriptionQuery(buyerID);
  $: emptyStateData = getEmptyStateData($subscriptionQuery.data);
  const {
    contentAttrs,
    underlayAttrs,
    open: isUploadInvoiceOpen,
    triggerElement,
    ...modal
  } = createModal();

  function getEmptyStateData(data: GetSubscriptionResponse | undefined): EmptyStateData {
    const copy: EmptyStateData = {
      title: "",
      subtitle: "",
      status: t("subscription.emptyState.noStatus") as EmptyStateData["status"],
    };

    if (!data) {
      return copy;
    }

    const { plan, status } = data;

    if (hasMinPremiumSubscription(plan, status)) {
      copy.title = t("premiumSubscription.emptyState.title");
      copy.subtitle = t("premiumSubscription.emptyState.subtitle");
      copy.status = t("premiumSubscription.emptyState.status") as EmptyStateData["status"];
    } else if (hasInactiveSubscription(status)) {
      copy.title = t("inactiveSubscription.emptyState.title");
      copy.subtitle = t("inactiveSubscription.emptyState.subtitle");
      copy.status = t("inactiveSubscription.emptyState.status") as EmptyStateData["status"];
    } else {
      switch (variant) {
        case "overview":
          copy.title = t("overview.emptyState.title");
          copy.subtitle = t("overview.emptyState.upload.subtitle");
          break;
        case "invoices":
          copy.title = t("invoices.emptyState.title");
          copy.subtitle = t("invoices.emptyState.subtitle");
          break;
        case "priceHistory":
          copy.title = t("priceHistory.emptyState.title");
          copy.subtitle = t("priceHistory.emptyState.subtitle");
        case "priceHistoryChangesOnly":
          copy.title = t("priceHistory.changesOnly.emptyState.title");
          copy.subtitle = t("priceHistory.changesOnly.emptyState.subtitle");
      }
    }

    return copy;
  }

  function handleCTAClick(
    data: GetSubscriptionResponse | undefined,
    emptyStateData: EmptyStateData
  ) {
    if (!data) {
      return;
    }
    if (emptyStateData.status === "inactive") {
      window.open(STRIPE_CUSTOMER_PORTAL_URL, "_blank");
      return;
    }
    openSubscriptionModal(data.plan, data.status, modal.onOpen);
  }

  const imagesByEmptyStateStatus = {
    active: ListIcon,
    none: {
      overview: WhiteboardIcon,
      invoices: DigitizeIcon,
      priceHistory: PriceAlertIcon,
      priceHistoryChangesOnly: PriceAlertIcon,
    },
    inactive: CreditCardIcon,
  };

  $: ImageComponent =
    emptyStateData.status === "none"
      ? imagesByEmptyStateStatus[emptyStateData.status][variant]
      : imagesByEmptyStateStatus[emptyStateData.status];
</script>

<EmptyStateBasic title={emptyStateData.title} subtitle={emptyStateData.subtitle}>
  <svelte:component this={ImageComponent} slot="illustration" />
  <svelte:fragment slot="cta">
    {#if $subscriptionQuery.data}
      <ButtonNew
        size="xs"
        on:click={() => {
          handleCTAClick($subscriptionQuery.data, emptyStateData);
        }}
        bind:element={$triggerElement}
      >
        {emptyStateData.status === "inactive" ? "edit payment method" : "upload invoice"}
      </ButtonNew>
    {/if}
  </svelte:fragment>
</EmptyStateBasic>

<InvoiceUploadModal
  contentAttrs={$contentAttrs}
  underlayAttrs={$underlayAttrs}
  open={$isUploadInvoiceOpen}
  contentAction={modal.content}
  onClose={modal.onClose}
/>
